/**
 * Pure geometry for the metrics page's charts: turning a day's worth of already-
 * computed numbers into an SVG path or a bar's height, and nothing about the
 * numbers themselves.
 *
 * Every derivation the charts draw - medians, p90s, which day a merge landed on,
 * how many PRs were waiting - lives in `@dyspatch/core`'s `HubMetrics.series`,
 * computed once beside the tiles that read the same rows (see
 * `packages/core/src/metrics.ts`). This module only maps those finished numbers
 * onto a drawing surface, which is why nothing here reads a `HubMetrics` or a
 * `Task`: a second derivation of a number the hub already computed is exactly
 * how a tile and a chart end up disagreeing, and pure geometry cannot do that.
 */

/** The largest value across one or more day-series, at least 1 so an
 * all-zero window still gets a scale that draws a flat line at the bottom
 * rather than dividing by zero. */
export function niceMax(...values: readonly (readonly number[])[]): number {
  let max = 0;
  for (const series of values) for (const value of series) max = Math.max(max, value);
  return max <= 0 ? 1 : max;
}

/** Linear map from a value in `[0, domainMax]` to a pixel in `[0, range]`. */
export function scale(value: number, domainMax: number, range: number): number {
  if (domainMax <= 0) return 0;
  return (value / domainMax) * range;
}

/** Whether any point in a count series is worth drawing an axis for. */
export function hasCounts(points: readonly { count: number }[]): boolean {
  return points.some((p) => p.count > 0);
}

/**
 * A short label for a day bucket: the weekday for a week-wide window, where a
 * reader knows "Tue" without counting back, and `d Mon` once the window is
 * wide enough that weekdays repeat and stop being useful.
 */
export function dayLabel(iso: string, days: number): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return days <= 7
    ? date.toLocaleDateString(undefined, { weekday: 'short' })
    : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** One pixel position per day, evenly spaced across `width`. */
function xOf(index: number, count: number, width: number): number {
  if (count <= 1) return width / 2;
  return (index / (count - 1)) * width;
}

/**
 * An SVG path through one value per day, `null` where a day has nothing
 * measured. A run of `null`s breaks the line rather than interpolating across
 * it - a gap in the data reads as a gap on the chart, not as a guess.
 */
export function linePath(
  values: readonly (number | null)[],
  width: number,
  height: number,
  domainMax: number,
): string {
  let d = '';
  let open = false;
  values.forEach((value, i) => {
    if (value === null) {
      open = false;
      return;
    }
    const x = xOf(i, values.length, width);
    const y = height - scale(value, domainMax, height);
    d += `${open ? 'L' : 'M'}${x.toFixed(2)},${y.toFixed(2)} `;
    open = true;
  });
  return d.trim();
}

/**
 * The filled band between two per-day series (e.g. the median and the p90),
 * as one closed polygon per unbroken run of days that have both. `high` is
 * expected at or above `low` on every day it is not skipping, which is true of
 * a p90 against its own median by construction.
 */
export function bandPath(
  low: readonly (number | null)[],
  high: readonly (number | null)[],
  width: number,
  height: number,
  domainMax: number,
): string {
  const n = Math.min(low.length, high.length);
  let path = '';
  let i = 0;
  while (i < n) {
    if (low[i] === null || high[i] === null) {
      i += 1;
      continue;
    }
    const start = i;
    while (i < n && low[i] !== null && high[i] !== null) i += 1;
    const end = i;
    let d = '';
    for (let j = start; j < end; j++) {
      const x = xOf(j, n, width);
      const y = height - scale(high[j]!, domainMax, height);
      d += `${j === start ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)} `;
    }
    for (let j = end - 1; j >= start; j--) {
      const x = xOf(j, n, width);
      const y = height - scale(low[j]!, domainMax, height);
      d += `L${x.toFixed(2)},${y.toFixed(2)} `;
    }
    path += `${d}Z `;
  }
  return path.trim();
}
